import { gql, useQuery } from '@apollo/client';
import { Badge, Button, Spinner } from '@apollo/orbit';
import React from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';
import {
  explorerRouteConfig,
  schemaRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { OrgSwitcher } from 'src/components/appNav/orgSwitcher/OrgSwitcher';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { BuildPipelineTrack, GraphType } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { CollectionsFeedbackCallout } from '../globalHeader/CollectionsFeedbackCallout';

import { ContractBadge } from './contractGraphBadge/ContractGraphBadge';
import {
  FederatedGraphWidget,
  FederatedGraphWidgetGraphVariantFragment,
} from './federatedGraphWidget/FederatedGraphWidget';
import {
  GraphVariantSwitcher,
  GraphVariantSwitcherFragment,
} from './graphVariantSwitcher/GraphVariantSwitcher';
import { InitiatingCloudEndpointBadge } from './initiatingCloudEndpointBadge/InitiatingCloudEndpointBadge';
import { PublicGraphWidget } from './publicGraphWidget/PublicGraphWidget';

const globalHeaderGraphContentQuery = gql<
  GraphQLTypes.GlobalHeaderGraphContentQuery,
  GraphQLTypes.GlobalHeaderGraphContentQueryVariables
>`
  query GlobalHeaderGraphContentQuery($graphId: ID!, $graphVariant: String!) {
    me {
      id
    }
    service(id: $graphId) {
      id
      title
      graphType
      roles {
        canQueryPrivateInfo
      }
      variant(name: $graphVariant) {
        id
        isContract
        compositionVersion
        permissions {
          canQueryBuildConfig
        }
        hasManagedSubgraphs
        ...FederatedGraphWidgetGraphVariantFragment
      }
      account {
        id
        ...GraphVariantSwitcherFragment
      }
    }
  }
  ${FederatedGraphWidgetGraphVariantFragment}
  ${GraphVariantSwitcherFragment}
`;

/**
 * Component representing the content for the GlobalHeader to be rendered when a
 * Graph has been specified
 */
export const GlobalHeaderGraphContent = ({
  currentGraphRef,
}: {
  currentGraphRef: GraphRef;
}) => {
  const location = useLocation();
  const graphVisibilityType = useGraphVisibility();

  const { data, error, loading, refetch } = useQuery(
    globalHeaderGraphContentQuery,
    {
      variables: {
        graphId: currentGraphRef?.graphId,
        graphVariant: currentGraphRef?.graphVariant,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const service = data?.service;
  const build = service?.variant?.latestLaunch?.build;
  const buildErrors =
    build?.result?.__typename === 'BuildFailure'
      ? build.result.errorMessages
      : [];
  const compositionVersion =
    service?.variant?.compositionVersion === BuildPipelineTrack.FED_NEXT &&
    service.variant.latestLaunch?.buildInput?.__typename ===
      'CompositionBuildInput'
      ? service.variant.latestLaunch.buildInput.version
      : service?.variant?.compositionVersion;

  const graphs = service?.account?.services ?? [];

  const [hasDismissedOperationCollectionsFeatureCallOut] = useLocalStorage(
    'hasDismissedOperationCollectionsFeatureCallOut',
  );

  const isExplorerRoute =
    useRouteMatch(explorerRouteConfig.definition) !== null;
  const isReadmeRoute =
    useRouteMatch(variantHomepageRouteConfig.definition) !== null;
  const isSchemaRoute = useRouteMatch(schemaRouteConfig.definition) !== null;
  const isCloudSupergraph = service?.graphType === GraphType.CLOUD_SUPERGRAPH;

  if (loading) {
    return <Spinner size="tiny" />;
  }

  if (error) {
    return (
      <div className="text-error">
        Could not load graph{' '}
        <Button size="sm" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!service?.roles?.canQueryPrivateInfo) {
    return null;
  }

  if (graphVisibilityType === 'public') {
    return (
      <>
        <div className="mx-3 text-sm font-semibold">
          {currentGraphRef.graphId} / {currentGraphRef.graphVariant}
        </div>
        <PublicGraphWidget graphRef={currentGraphRef} />
      </>
    );
  }

  if (!service.account) {
    return (
      <div className="text-error">
        Could not find this graph{' '}
        <Button size="sm" onClick={() => refetch()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <>
      <OrgSwitcher collapseOrgName={true} />

      <PermissionGuard
        graphId={service.id}
        graphPermissions="canQuerySchemas"
        fallback={null}
      >
        <GraphVariantSwitcher
          graphs={graphs}
          currentGraphRef={currentGraphRef}
          currentGraphTitle={service.title}
          graphVisibilityType={graphVisibilityType}
        />
        {!service.variant?.isContract &&
          service?.variant?.hasManagedSubgraphs &&
          // we don't show the federated badge on cloud supergraphs since they are all federated
          !isCloudSupergraph && (
            <FederatedGraphWidget
              compositionVersion={compositionVersion}
              buildErrors={buildErrors}
            />
          )}
        {service.variant?.permissions.canQueryBuildConfig &&
          service.variant?.isContract && (
            <ContractBadge
              graphId={currentGraphRef.graphId}
              graphVariant={currentGraphRef.graphVariant}
            />
          )}
        {isCloudSupergraph && isMinLengthArray(1, buildErrors) && (
          <Link
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.buildErrors,
            })}
          >
            <Badge className="ml-2" variant="error">
              BUILD FAILED
            </Badge>
          </Link>
        )}
        {isCloudSupergraph && <InitiatingCloudEndpointBadge />}
        <PublicGraphWidget graphRef={currentGraphRef} />
      </PermissionGuard>

      {hasDismissedOperationCollectionsFeatureCallOut &&
        (isExplorerRoute || isReadmeRoute || isSchemaRoute) && (
          <>
            <div className="flex-1" />
            <CollectionsFeedbackCallout />
          </>
        )}
    </>
  );
};
